// Non-destructive dry-run preview: given a sample of the trigger's data, trace what each step WOULD do
// (variables substituted, branch/filter decisions evaluated) without creating records or sending
// anything. Pure functions — unit-tested in preview.spec.ts.
import type { Edge } from '@xyflow/react'
import { parseTokenFallback, TOKEN_PATTERN } from 'app/lib/messageTemplates'
import type { ListCopy } from 'lib/listCopy'
import type { Condition, ConditionMatch } from './conditions'
import { editTaskSummary } from './format'
import type { WorkflowNode } from 'app/types/workflows'

/** Fill `{{key}}` / `{{key ?? "fallback"}}` tokens from the sample. Missing
 *  values use the fallback when one is present; otherwise `[key]` keeps gaps
 *  visible. Borrows the shared GRAMMAR and fallback parsing from the token lib,
 *  but not its canonical resolution: keys here are looked up in the builder's
 *  own sample data. */
export function fillVariables(text: string, sample: Record<string, string>): string {
  TOKEN_PATTERN.lastIndex = 0
  return text.replace(
    TOKEN_PATTERN,
    (_match, key: string, separator: string | undefined, rawFallback: string | undefined) => {
      // The builder's sample data is still keyed by the engine's old flat names
      // (`firstName`), while node text is namespaced (`contact.firstName`) since
      // trackable-api#478. Try the namespaced key, then its last segment, so the
      // preview stays truthful either side of the builder's own key update.
      const value = sample[key] ?? sample[key.split(`.`).pop() ?? key]
      // An EMPTY value counts as missing, not as a value (TRK-5064 / TRK-5468). The engine falls back
      // on a blank field, so a preview that renders "Hi !" for a contact with no first name is lying
      // about the one thing the author is checking. `!= null` alone reintroduces exactly that bug.
      if (value) return value
      return parseTokenFallback(separator, rawFallback) || `[${key}]`
    },
  )
}

/** Evaluate one field condition against the sample. Returns undefined when it can't be determined
 *  (event predicate, or a value operator with no sample value present for a "contains"/"is" that needs
 *  one — we still compare, but a missing sample field just means the comparison runs on ""). */
function evalCondition(condition: Condition, sample: Record<string, string>): boolean | undefined {
  if (condition.type === `event`) return undefined
  if (!condition.field) return undefined
  const value = sample[condition.field] ?? ``
  switch (condition.operator) {
    case `is`:
      return value === (condition.value ?? ``)
    case `is_not`:
      return value !== (condition.value ?? ``)
    case `contains`:
      return value.toLowerCase().includes((condition.value ?? ``).toLowerCase())
    case `is_set`:
      return value !== ``
    case `is_empty`:
      return value === ``
    default:
      return undefined
  }
}

/** Evaluate a Filter/Branch condition group against the sample. Undeterminable conditions (events) are
 *  skipped; an empty/all-skipped group is treated as passing. */
export function evaluateConditions(
  conditions: Condition[],
  match: ConditionMatch,
  sample: Record<string, string>,
): boolean {
  const results = conditions.map((c) => evalCondition(c, sample)).filter((r): r is boolean => r !== undefined)
  if (!results.length) return true
  return match === `any` ? results.some(Boolean) : results.every(Boolean)
}

export type PreviewStep = {
  id: string
  kind: WorkflowNode[`type`]
  /** One-line "what happens" with variables filled. */
  detail: string
  /** A branch/filter note ("sample takes Yes", "sample stops here"), if relevant. */
  note?: string
}

const q = (text: string) => `“${text}”`

/** One node → its preview detail, with the sample substituted into any text it produces. */
function previewNode(node: WorkflowNode, sample: Record<string, string>, copy: ListCopy): string {
  switch (node.type) {
    case `sendSms`:
      return `Text the contact: ${q(fillVariables(node.data.message, sample))}`
    case `initiateCall`:
      return `Call the contact${node.data.ringNumber ? ` (ring ${node.data.ringNumber})` : ``}${
        node.data.whisper ? ` · whisper: ${q(fillVariables(node.data.whisper, sample))}` : ``
      }`
    case `createTask`:
      return `Create task: ${q(fillVariables(node.data.title, sample))}`
    case `editTask`:
      return `Edit task: ${editTaskSummary(node.data)}`
    case `tagObject`:
      return `${node.data.operation === `remove` ? `Remove` : `Add`} tags: ${node.data.tags.join(`, `) || `none`}`
    case `addToPipeline`:
      return `${node.data.operation === `move` ? `Move the contact to` : `Add the contact to`} ${node.data.pipelineName ?? copy.noun}${node.data.stage ? ` · ${node.data.stage}` : ``}`
    case `editObject`:
      return (
        node.data.fields
          .map((row) =>
            row.operation === `clear`
              ? `Clear ${row.field ?? `field`}`
              : `Set ${row.field ?? `field`} = ${q(fillVariables(row.value, sample))}`,
          )
          .join(`, `) || `Update contact (no fields)`
      )
    case `updateAppointment`:
      return `Mark the appointment confirmed`
    case `assignOwner`:
      return `Assign owner${node.data.ownerName ? ` to ${node.data.ownerName}` : ``}`
    case `addNote`:
      return `Add note: ${q(fillVariables(node.data.body, sample))}`
    case `logFollowUp`:
      return `Log a follow-up: ${q(fillVariables(node.data.note, sample))}`
    case `sendPaperwork`:
      return `Text the contact ${node.data.packetName ?? `a paperwork packet`}: ${q(fillVariables(node.data.message, sample))}`
    case `sendNotification`:
      return `Notify ${node.data.recipients.join(`, `) || `team`}: ${q(fillVariables(node.data.message, sample))}`
    case `waitUntil`:
      return `Wait up to ${node.data.duration} ${node.data.unit} for the contact to match`
    case `wait`:
      return `Wait ${node.data.duration} ${node.data.unit}`
    case `waitForReply`:
      return `Wait for a reply${node.data.within ? ` (up to ${node.data.within} ${node.data.unit ?? `days`})` : ``}`
    case `waitForKeyword`: {
      const options = node.data.options.map((option, index) => {
        const name = option.label.trim() || `Option ${index + 1}`
        const quoted = option.keywords.map((word) => `"${word}"`)
        const last = quoted.pop()
        return last ? `${name} (${quoted.length ? `${quoted.join(`, `)} or ${last}` : last})` : name
      })
      const listed = options.length ? `: ${options.join(`, `)}` : ``
      return `Wait for a reply${listed}${node.data.within ? ` (up to ${node.data.within} ${node.data.unit ?? `days`})` : ``}`
    }
    case `waitForCallResult`:
      return `Wait for the call result`
    case `branch`:
      return `Branch`
    case `filter`:
      return `Filter`
    case `exit`:
      return `Exit the workflow`
    default:
      return node.type
  }
}

/** Pick the outgoing edge to follow from a node, given the sample. Branch follows Yes/No by evaluating
 *  its conditions; everything else takes its single downstream edge. */
function nextEdge(node: WorkflowNode | undefined, outgoing: Edge[], sample: Record<string, string>): Edge | undefined {
  // The task path runs alongside the main path; a single traced path follows the main one.
  if (node?.type === `createTask`) return outgoing.find((e) => !e.sourceHandle) ?? outgoing[0]
  if (node?.type === `branch` || node?.type === `waitUntil`) {
    const matched = evaluateConditions(node.data.conditions, node.data.match, sample)
    const wanted = matched ? `yes` : `no`
    return outgoing.find((e) => e.sourceHandle === wanted) ?? outgoing.find((e) => e.sourceHandle !== wanted)
  }
  return outgoing[0]
}

/** Trace the path this sample takes from the trigger, producing an ordered per-step preview. Follows
 *  branch/filter decisions; stops at a failed filter, an exit, or a leaf. Non-destructive. */
export function buildPreview(
  nodes: WorkflowNode[],
  edges: Edge[],
  sample: Record<string, string>,
  copy: ListCopy,
): PreviewStep[] {
  const byId = new Map(nodes.map((n) => [n.id, n]))
  const trigger = nodes.find((n) => n.type === `trigger`)
  if (!trigger) return []

  const steps: PreviewStep[] = []
  const seen = new Set<string>([trigger.id])
  let current: WorkflowNode | undefined = trigger

  while (current) {
    const next = nextEdge(
      current,
      edges.filter((e) => e.source === current!.id),
      sample,
    )
    const target = next && byId.get(next.target)
    if (!target || seen.has(target.id)) break
    seen.add(target.id)

    if (target.type === `branch`) {
      const matched = evaluateConditions(target.data.conditions, target.data.match, sample)
      steps.push({
        id: target.id,
        kind: target.type,
        detail: `Branch`,
        note: `Sample takes the ${matched ? `Yes` : `No`} path`,
      })
    } else if (target.type === `filter`) {
      const pass = evaluateConditions(target.data.conditions, target.data.match, sample)
      steps.push({
        id: target.id,
        kind: target.type,
        detail: `Filter`,
        note: pass ? `Sample passes, so it continues` : `Sample doesn’t match, so it stops here`,
      })
      if (!pass) break
    } else {
      steps.push({ id: target.id, kind: target.type, detail: previewNode(target, sample, copy) })
      if (target.type === `exit`) break
    }
    current = target
  }
  return steps
}
